import type { UseQueryResult } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Building2, User, Users } from 'lucide-react';
import type { SearchResponse } from './types.js';

interface SearchResultsProps {
  search: UseQueryResult<SearchResponse, Error>;
  debouncedQ: string;
  totalResults: number;
  onClose: () => void;
}

export const SearchResults = ({
  search,
  debouncedQ,
  totalResults,
  onClose,
}: SearchResultsProps) => (
  <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[min(24rem,60vh)] overflow-y-auto rounded-md border border-border bg-surface p-2 shadow-lg">
    {search.isLoading ? (
      <p className="p-3 text-sm text-muted-fg">Buscando…</p>
    ) : totalResults === 0 ? (
      <p className="p-3 text-sm text-muted-fg">Nenhum resultado para “{debouncedQ}”.</p>
    ) : (
      <>
        {search.data && search.data.owners.length > 0 && (
          <Section title="Donos" icon={<User className="h-3 w-3" />}>
            {search.data.owners.map((o) => (
              <ResultLink key={o.id} to="/clients" onClick={onClose}>
                <span className="truncate font-medium">{o.name}</span>
                <span className="truncate text-xs text-muted-fg">{o.email}</span>
              </ResultLink>
            ))}
          </Section>
        )}
        {search.data && search.data.lots.length > 0 && (
          <Section title="Estacionamentos" icon={<Building2 className="h-3 w-3" />}>
            {search.data.lots.map((l) => (
              <ResultLink key={l.id} to="/clients" onClick={onClose}>
                <span className="truncate font-medium">{l.name}</span>
                <span className="truncate text-xs text-muted-fg">
                  {l.city && l.state ? `${l.city} / ${l.state} · ` : ''}
                  {l.id.slice(0, 8)}
                </span>
              </ResultLink>
            ))}
          </Section>
        )}
        {search.data && search.data.operators.length > 0 && (
          <Section title="Operadores" icon={<Users className="h-3 w-3" />}>
            {search.data.operators.map((o) => (
              <ResultLink key={o.id} to="/clients" onClick={onClose}>
                <span className="truncate font-medium">{o.name}</span>
                <span className="truncate text-xs text-muted-fg">{o.phone ?? '—'}</span>
              </ResultLink>
            ))}
          </Section>
        )}
      </>
    )}
  </div>
);

interface SectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

const Section = ({ title, icon, children }: SectionProps) => (
  <div className="mb-1 last:mb-0">
    <div className="flex items-center gap-1 px-2 py-1 text-xs font-medium uppercase tracking-wide text-muted-fg">
      {icon}
      {title}
    </div>
    <ul>{children}</ul>
  </div>
);

interface ResultLinkProps {
  to: string;
  onClick: () => void;
  children: React.ReactNode;
}

const ResultLink = ({ to, onClick, children }: ResultLinkProps) => (
  <li>
    <Link
      to={to}
      onClick={onClick}
      className="flex min-w-0 flex-col gap-0.5 rounded px-3 py-2 text-sm hover:bg-muted/60"
    >
      {children}
    </Link>
  </li>
);
